import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Member } from '../domain/audience';
import type { Collection } from '../domain/collections';
import { useLoad } from '../hooks/useLoad';
import { useRepositories } from '../hooks/useRepositories';
import { PersonName } from '../components/PersonDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { ActionError, LoadingState, StateForError } from '../components/PageStates';
import UserAvatar from '../components/UserAvatar';
import { fullName } from '../utils/people';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

/**
 * `/collections/:id/members` — who is in a collection, owner first. The
 * owner of a private collection can take one person out of it without
 * ending the connection (015 FR-009, FR-010).
 */
export default function CollectionMembersPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { audience, collections } = useRepositories();
  const page = useLoad<{ collection: Collection; members: Member[] }>(async () => {
    const [collection, members] = await Promise.all([collections.get(id), audience.members(id)]);
    return { collection, members };
  }, id);
  const [removing, setRemoving] = useState<Member | null>(null);
  const [error, setError] = useState<unknown>(null);

  if (page.status === 'loading') return <LoadingState label="Loading members" />;
  if (page.status === 'error') return <StateForError error={page.error} onRetry={page.reload} />;

  const { collection, members } = page.data;
  const canRemove = collection.myRelation === 'owner' && collection.visibility === 'private';
  const others = members.filter((m) => m.role !== 'owner');

  const remove = async (member: Member) => {
    setError(null);
    try {
      await audience.removeMember(collection.id, member.person.id);
      page.reload();
    } catch (e) {
      setError(e);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div>
        <Link
          to={`/collections/${collection.id}`}
          className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-primary"
        >
          <ArrowLeft className="size-4" />
          Back to the collection
        </Link>
        <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
          Members
        </p>
        <h1 className="text-4xl font-bold tracking-tight text-foreground">{collection.name}</h1>
      </div>

      {error !== null && <ActionError error={error} />}

      <ul aria-label="Members" className="space-y-3">
        {members.map((m) => {
          const name = fullName(m.person);
          return (
            <li key={m.person.id} className="flex items-center gap-4 rounded-3xl bg-card p-5">
              <UserAvatar person={m.person} className="size-12" textClassName="text-base" />
              <p className="min-w-0 flex-1 font-semibold text-foreground">
                <PersonName person={m.person} onBlocked={page.reload} />
              </p>
              {m.role === 'owner' ? (
                <span className="rounded-full bg-wood/15 px-3 py-1 text-xs font-semibold text-wood">
                  Owner
                </span>
              ) : (
                canRemove && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-full"
                    aria-label={`Remove ${name} from this collection`}
                    onClick={() => setRemoving(m)}
                  >
                    Remove from this collection
                  </Button>
                )
              )}
            </li>
          );
        })}
      </ul>

      {others.length === 0 && (
        <p className="rounded-3xl bg-card px-6 py-8 text-muted-foreground">
          No one else is in this collection yet.
        </p>
      )}

      {removing && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setRemoving(null)}
          title={`Remove ${fullName(removing.person)} from ${collection.name}?`}
          description={
            <p>
              {removing.person.firstName} will no longer see {collection.name} or its listings.
              You'll stay connected. Only a personal invite from you can bring{' '}
              {removing.person.firstName} back to it.
            </p>
          }
          confirmLabel="Remove"
          onConfirm={() => remove(removing)}
        />
      )}
    </div>
  );
}
